import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../i18n/I18nProvider";

interface DateOfBirthSelectProps {
  id: string;
  value: string; // ISO "YYYY-MM-DD" or ""
  onChange: (value: string) => void;
  maxYearsAgo?: number;
}

const pad2 = (value: number) => String(value).padStart(2, "0");

const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

const parseValue = (value: string) => {
  const [year, month, day] = value ? value.split("-") : ["", "", ""];
  return { year: year ?? "", month: month ?? "", day: day ?? "" };
};

export const DateOfBirthSelect: React.FC<DateOfBirthSelectProps> = ({
  id,
  value,
  onChange,
  maxYearsAgo = 100,
}) => {
  const { t } = useTranslation();
  const monthNames = t("wizard.common.dateOfBirth.months") as Record<string, string>;

  const [year, setYear] = useState(() => parseValue(value).year);
  const [month, setMonth] = useState(() => parseValue(value).month);
  const [day, setDay] = useState(() => parseValue(value).day);

  // Re-sync local selects when the value is set/reset from outside
  // (e.g. loading an existing lead, or switching between household members).
  useEffect(() => {
    const parsed = parseValue(value);
    setYear(parsed.year);
    setMonth(parsed.month);
    setDay(parsed.day);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () =>
      Array.from({ length: maxYearsAgo + 1 }, (_, index) => String(currentYear - index)),
    [currentYear, maxYearsAgo]
  );

  const months = useMemo(
    () => Array.from({ length: 12 }, (_, index) => pad2(index + 1)),
    []
  );

  const dayCount = year && month ? daysInMonth(Number(year), Number(month)) : 31;
  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, index) => pad2(index + 1)),
    [dayCount]
  );

  const handleChange = (nextYear: string, nextMonth: string, nextDay: string) => {
    setYear(nextYear);
    setMonth(nextMonth);
    setDay(nextDay);

    if (nextYear && nextMonth && nextDay) {
      const clampedDay = pad2(Math.min(Number(nextDay), daysInMonth(Number(nextYear), Number(nextMonth))));
      onChange(`${nextYear}-${nextMonth}-${clampedDay}`);
    } else {
      onChange("");
    }
  };

  return (
    <div className="date-of-birth-select">
      <select
        id={`${id}-day`}
        aria-label={t("wizard.common.dateOfBirth.day")}
        value={day}
        onChange={(event) => handleChange(year, month, event.target.value)}
      >
        <option value="">{t("wizard.common.dateOfBirth.day")}</option>
        {days.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>

      <select
        id={`${id}-month`}
        aria-label={t("wizard.common.dateOfBirth.month")}
        value={month}
        onChange={(event) => handleChange(year, event.target.value, day)}
      >
        <option value="">{t("wizard.common.dateOfBirth.month")}</option>
        {months.map((m) => (
          <option key={m} value={m}>
            {monthNames?.[m] ?? m}
          </option>
        ))}
      </select>

      <select
        id={`${id}-year`}
        aria-label={t("wizard.common.dateOfBirth.year")}
        value={year}
        onChange={(event) => handleChange(event.target.value, month, day)}
      >
        <option value="">{t("wizard.common.dateOfBirth.year")}</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
};
